import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    AutocompleteElement,
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import { DeleteForever } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    IngredientInput,
    ProductInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import {
    CREATE_STANDARD_DRINK_RECIPE_ITEM_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { StandardDrink, StandardDrinkRecipeItem } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';
import { DRINK_RECIPE_ITEM_TYPE, FLAVOR_TYPES } from '@barboxx/types';
import { enumToFormOptions, enumToList, getProductName } from '@barboxx/utils';

const COLUMNS: GridColDef<StandardDrinkRecipeItem>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },
    {
        field: 'type',
        headerName: 'Type',
        minWidth: NAME_MIN_WIDTH,
        type: 'singleSelect',
        valueOptions: enumToFormOptions(DRINK_RECIPE_ITEM_TYPE),
    },

    {
        field: 'name',
        headerName: 'Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 2,
        valueGetter: (value, row, col) => {
            const { type } = row;

            switch (type) {
                case DRINK_RECIPE_ITEM_TYPE.ProductType:
                    return row.productType?.name;
                case DRINK_RECIPE_ITEM_TYPE.ProductSubtype:
                    return row.productSubtype?.name;
                case DRINK_RECIPE_ITEM_TYPE.Product:
                    return getProductName(row.product);
                case DRINK_RECIPE_ITEM_TYPE.Ingredient:
                    return row.ingredient?.name;
            }
        },
    },
    {
        field: 'quantity',
        headerName: 'Quantity',
        minWidth: NAME_MIN_WIDTH,
        flex: 2,
        editable: true,
        valueGetter: (value, row, col) => {
            const { unit } = row;
            if (!value) {
                return '';
            }
            return `${value}${unit ? ` ${unit}` : ''}`;
        },
    },
    {
        field: 'flavor',
        headerName: 'Flavor',
        type: 'singleSelect',
        valueOptions: enumToFormOptions(FLAVOR_TYPES),
        width: 200,
    },
    {
        field: 'isOptional',
        headerName: 'Is Optional',
        minWidth: NAME_MIN_WIDTH,
        type: 'boolean',
        editable: true,
    },
    {
        field: 'priority',
        headerName: 'Priority',
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        editable: true,
    },
];

interface Props {
    standardDrink: StandardDrink.DetailsResponse;
    refetch: () => void;
}

export const RecipeItemListCard = ({ standardDrink, refetch }: Props) => {
    const { recipeItems, id: standardDrinkId } = standardDrink;
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<StandardDrinkRecipeItem.CreateRequest>({
        resolver: yupResolver(CREATE_STANDARD_DRINK_RECIPE_ITEM_SCHEMA),
        defaultValues: {
            standardDrinkId,
        },
    });

    const mutateCreateProduct = useMutation({
        mutationKey: ['standardDrinkRecipeItems'],
        mutationFn: (data: StandardDrinkRecipeItem.CreateRequest) =>
            Api.StandardDrinks.RecipeItem.postStandardDrinkRecipeItem(data),
    });

    const onSubmit = async (values: StandardDrinkRecipeItem.CreateRequest) => {
        return mutateCreateProduct
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created  recipe item!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating recipe item!', {
                    severity: 'error',
                });
            });
    };

    const mutateDeleteRecipeItem = useMutation({
        mutationKey: ['deleteStandardDrinkRecipeItem'],
        mutationFn: (data: StandardDrinkRecipeItem.DeleteRequest) =>
            Api.StandardDrinks.RecipeItem.deleteStandardDrinkRecipeItem(data),
    });

    const handleDeleteRecipeItem = async (
        values: StandardDrinkRecipeItem.DeleteRequest
    ) => {
        return mutateDeleteRecipeItem
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Deleted recipe item!`, {
                    severity: 'success',
                });
                refetch();
            })
            .catch(e => {
                notifications.show('Error deleting recipe item!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams) => {
        let url = '';
        switch (params.row.type) {
            case DRINK_RECIPE_ITEM_TYPE.ProductType:
                url = `/products/types/${params.row.productTypeId}`;
                break;
            case DRINK_RECIPE_ITEM_TYPE.ProductSubtype:
                url = `/products/subtypes/${params.row.productSubtypeId}`;
                break;
            case DRINK_RECIPE_ITEM_TYPE.Product:
                url = `/products/${params.row.productId}`;
                break;
            case DRINK_RECIPE_ITEM_TYPE.Ingredient:
                url = `/core/ingredients/${params.row.ingredientId}`;
                break;
        }
        navigate(url);
    };

    const values = useWatch(formContext) as StandardDrinkRecipeItem.Base;

    const columns = COLUMNS.concat([
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: NAME_MIN_WIDTH,
            type: 'actions',
            getActions: params => [
                // <Button
                //     key="edit"
                //     onClick={() => {
                //         navigate(`${params.id}`);
                //     }}
                // >
                //     Edit
                // </Button>,
                <IconButton
                    key="delete"
                    onClick={() => {
                        handleDeleteRecipeItem(params.row);
                        // Handle delete action
                    }}
                >
                    <DeleteForever />
                </IconButton>,
            ],
        },
    ]);

    return (
        <>
            <DataGridCard
                title="Recipe Items List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Recipe Item
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={recipeItems}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Standard Drink Recipe Item
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <SelectElement
                            label="Type"
                            name="type"
                            fullWidth
                            options={enumToFormOptions(DRINK_RECIPE_ITEM_TYPE)}
                        />
                        {values.type === DRINK_RECIPE_ITEM_TYPE.ProductType && (
                            <ProductTypeInput />
                        )}
                        {values.type ===
                            DRINK_RECIPE_ITEM_TYPE.ProductSubtype && (
                            <ProductSubtypeInput />
                        )}
                        {values.type === DRINK_RECIPE_ITEM_TYPE.Product && (
                            <ProductInput />
                        )}
                        {values.type === DRINK_RECIPE_ITEM_TYPE.Ingredient && (
                            <IngredientInput />
                        )}
                        <TextFieldElement label="Unit" name="unit" fullWidth />
                        <TextFieldElement
                            label="Quantity"
                            name="quantity"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    step: 0.1,
                                    max: 10000,
                                },
                            }}
                            fullWidth
                        />
                        <TextFieldElement
                            label="Priority"
                            name="priority"
                            fullWidth
                        />
                        {(values.type === DRINK_RECIPE_ITEM_TYPE.ProductType ||
                            values.type ===
                                DRINK_RECIPE_ITEM_TYPE.ProductSubtype) && (
                            <AutocompleteElement
                                label="Flavor"
                                name="flavor"
                                options={enumToList(FLAVOR_TYPES)}
                            />
                        )}
                        <CheckboxElement
                            label="Is Optional"
                            name="isOptional"
                        />
                        <CheckboxElement
                            label="No Substitution"
                            name="noSubstitution"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
